/**
 * Timezone Service
 * Provides accurate time in the center's configured timezone
 */

// Cache configuration
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
let cachedTime = null;
let cacheExpiry = null;

/**
 * Get the center's configured timezone or default to Eastern
 * @returns {string} IANA timezone identifier
 */
function getCenterTimezone() {
  // Try to get timezone from config
  const config = window.centerConfig || {};
  return config.timezone || 'America/New_York';
}

/**
 * Get current time in center's timezone from API
 * @returns {Promise<Date>} Current date/time in center's timezone
 */
export async function getCenterTime() {
  const timezone = getCenterTimezone();
  
  try {
    // Check cache first
    if (cachedTime && cacheExpiry && Date.now() < cacheExpiry && cachedTime.timezone === timezone) {
      // Return cached time adjusted by elapsed time
      const elapsed = Date.now() - cachedTime.fetchedAt;
      const adjustedTime = new Date(cachedTime.datetime.getTime() + elapsed);
      return adjustedTime;
    }

    // Fetch from WorldTimeAPI
    const response = await fetch(`https://worldtimeapi.org/api/timezone/${timezone}`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch timezone data');
    }
    
    const data = await response.json();
    
    // Parse the datetime string
    const datetime = new Date(data.datetime);
    
    // Cache the result
    cachedTime = {
      datetime: datetime,
      fetchedAt: Date.now(),
      offset: data.utc_offset,
      isDST: data.dst,
      timezone: timezone
    };
    cacheExpiry = Date.now() + CACHE_DURATION;
    
    return datetime;
  } catch (error) {
    console.error(`Error fetching time for timezone ${timezone}:`, error);
    
    // Fallback: Use local time with center's timezone
    // This won't be perfectly accurate but better than nothing
    const now = new Date();
    const timeString = now.toLocaleString("en-US", {
      timeZone: timezone
    });
    return new Date(timeString);
  }
}

// Keep backward compatibility with old function name
export const getEasternTime = getCenterTime;

/**
 * Get today's date in center's timezone
 * @returns {Promise<Date>} Today's date at midnight in center's timezone
 */
export async function getCenterToday() {
  const centerTime = await getCenterTime();
  const today = new Date(centerTime);
  today.setHours(0, 0, 0, 0);
  return today;
}

// Backward compatibility
export const getEasternToday = getCenterToday;

/**
 * Check if a date is today in center's timezone
 * @param {Date|string} date - Date to check
 * @returns {Promise<boolean>} True if date is today in center's timezone
 */
export async function isToday(date) {
  const centerToday = await getCenterToday();
  const checkDate = new Date(date);
  checkDate.setHours(0, 0, 0, 0);
  return centerToday.getTime() === checkDate.getTime();
}

/**
 * Check if a date is in the future in center's timezone
 * @param {Date|string} date - Date to check
 * @returns {Promise<boolean>} True if date is in the future
 */
export async function isFuture(date) {
  const centerTime = await getCenterTime();
  const checkDate = new Date(date);
  return checkDate.getTime() > centerTime.getTime();
}

/**
 * Format date for display in center's timezone
 * @param {Date|string} date - Date to format
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string
 */
export function formatCenterDate(date, options = {}) {
  const timezone = getCenterTimezone();
  const dateObj = new Date(date);
  return dateObj.toLocaleString("en-US", {
    timeZone: timezone,
    ...options
  });
}

// Backward compatibility
export const formatEasternDate = formatCenterDate;

/**
 * Get relative time string (e.g., "2 hours ago", "in 3 days")
 * @param {Date|string} date - Date to compare
 * @returns {Promise<string>} Relative time string
 */
export async function getRelativeTime(date) {
  const centerTime = await getCenterTime();
  const checkDate = new Date(date);
  const diff = checkDate.getTime() - centerTime.getTime();
  
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  
  if (Math.abs(days) > 0) {
    return days > 0 ? `in ${days} day${days !== 1 ? 's' : ''}` : `${-days} day${days !== -1 ? 's' : ''} ago`;
  }
  if (Math.abs(hours) > 0) {
    return hours > 0 ? `in ${hours} hour${hours !== 1 ? 's' : ''}` : `${-hours} hour${hours !== -1 ? 's' : ''} ago`;
  }
  if (Math.abs(minutes) > 0) {
    return minutes > 0 ? `in ${minutes} minute${minutes !== 1 ? 's' : ''}` : `${-minutes} minute${minutes !== -1 ? 's' : ''} ago`;
  }
  return 'just now';
}

const timezoneService = {
  getCenterTime,
  getCenterToday,
  isToday,
  isFuture,
  formatCenterDate,
  getRelativeTime,
  getCenterTimezone,
  // Backward compatibility
  getEasternTime,
  getEasternToday,
  formatEasternDate
};

export default timezoneService;